import { components } from "@/api-schema";
import {
  ModelFamilyIdentifierProvider,
  PathsV1BrandSearchPostParametersQueryExtend,
  PathsV1BrandSearchPostParametersQuerySelect,
  PathsV1ModelFamilySearchPostParametersQueryExtend,
  PathsV1ModelFamilySearchPostParametersQuerySelect,
  PathsV1ModelFamilyUuidGetParametersQueryExtend,
} from "@/api-schema-enums";
import { useBrandSearchQuery } from "@/common/api/brand/use-brand-search.query";
import { useModelFamilyByUuidQuery } from "@/common/api/model-family/use-model-family-by-uuid.query";
import { useModelFamilyCreateMutation } from "@/common/api/model-family/use-model-family-create.mutation";
import { useModelFamilySearchQuery } from "@/common/api/model-family/use-model-family-search.query";
import { useModelFamilyUpdateMutation } from "@/common/api/model-family/use-model-family-update.mutation";
import { getModelFamilyDatId } from "@/common/helpers/get-model-family-dat-id";
import { createPlaceholderKeys } from "@/common/helpers/list-keys";
import { normalizeGeneratedOptionalString } from "@/common/helpers/normalize-generated-optional-string";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { Card, CardContent } from "@/components/ui/card";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Skeleton } from "@/components/ui/skeleton";
import { SystemInfoFields } from "@/components/system-info-fields/system-info-fields";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { z } from "zod";
import { getBrandDatIdentifier } from "../../brand/brand-identifiers";

type ModelFamily = components["schemas"]["ModelFamily"];

const VALIDATION_ERROR_DURATION = 5000;

function getNormalizedModelFamilyDatId(modelFamily?: ModelFamily | null): string | undefined {
  return normalizeGeneratedOptionalString(getModelFamilyDatId(modelFamily?.modelFamilyIdentifiers));
}

export const ModelFamilyFormSchema = (t: (key: string, options?: Record<string, unknown>) => string) =>
  z.object({
    name: z.string().trim().min(1, t("masterdata:ModelFamilyNameIsRequired")),
    datId: z.string().trim().optional(),
    brand: z.string().min(1, t("masterdata:BrandIsRequired")),
  });

export type ModelFamilyFormSchemaType = z.infer<ReturnType<typeof ModelFamilyFormSchema>>;

interface ModelFamilyFormFieldsProps {
  uuid?: string;
}

export function ModelFamilyFormFields({ uuid }: ModelFamilyFormFieldsProps = {}) {
  const isUpdateMode = !!uuid;
  const { t } = useTranslation(["masterdata", "common"]);
  const [_, navigate] = useLocation();

  const { data: modelFamily, isPending: isModelFamilyLoading } = useModelFamilyByUuidQuery(uuid ?? "", {
    parameters: {
      extend: [
        PathsV1ModelFamilyUuidGetParametersQueryExtend.Brand,
        PathsV1ModelFamilyUuidGetParametersQueryExtend.ModelFamilyIdentifiers,
      ],
    },
  });

  const form = useForm<ModelFamilyFormSchemaType>({
    resolver: zodResolver(ModelFamilyFormSchema(t)),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      datId: "",
      brand: "",
    },
    values:
      isUpdateMode && modelFamily
        ? {
            name: modelFamily.name || "",
            datId: getNormalizedModelFamilyDatId(modelFamily) || "",
            brand: modelFamily.brand?.uuid || "",
          }
        : undefined,
  });

  const createMutation = useModelFamilyCreateMutation();
  const updateMutation = useModelFamilyUpdateMutation();

  const [watchedName, watchedDatId, watchedBrand] = useWatch({
    control: form.control,
    name: ["name", "datId", "brand"],
  });

  const { data: brands, isPending: isBrandsLoading } = useBrandSearchQuery({
    options: {
      body: {},
      parameters: {
        pageSize: -1,
        select: [
          PathsV1BrandSearchPostParametersQuerySelect.Uuid,
          PathsV1BrandSearchPostParametersQuerySelect.Name,
          PathsV1BrandSearchPostParametersQuerySelect.BrandIdentifiers,
        ],
        extend: [PathsV1BrandSearchPostParametersQueryExtend.BrandIdentifiers],
      },
    },
  });

  const { data: existingModelFamilies, isPending: isExistingModelFamiliesLoading } = useModelFamilySearchQuery({
    options: {
      body: watchedBrand ? { brand: [watchedBrand] } : {},
      parameters: {
        pageSize: -1,
        select: [
          PathsV1ModelFamilySearchPostParametersQuerySelect.Uuid,
          PathsV1ModelFamilySearchPostParametersQuerySelect.Name,
          PathsV1ModelFamilySearchPostParametersQuerySelect.ModelFamilyIdentifiers,
        ],
        extend: [PathsV1ModelFamilySearchPostParametersQueryExtend.ModelFamilyIdentifiers],
      },
    },
    enabled: !!watchedBrand,
  });

  const brandOptions = useMemo<ComboboxOption[]>(() => {
    return (
      brands?.data?.map((brand) => {
        const datId = getBrandDatIdentifier(brand);

        return {
          value: brand.uuid,
          label: datId ? `${brand.name} · ${datId}` : brand.name,
          searchText: `${brand.name} ${datId ?? ""}`,
        };
      }) ?? []
    );
  }, [brands?.data]);

  const duplicateValidation = useMemo(() => {
    if (!existingModelFamilies?.data || !watchedBrand) {
      return {
        errors: [] as string[],
        hasErrors: false,
        hasDuplicateDatId: false,
        hasDuplicateName: false,
      };
    }

    const errors: string[] = [];
    let hasDuplicateName = false;
    let hasDuplicateDatId = false;

    if (watchedName?.trim()) {
      const normalizedName = watchedName.toLowerCase().trim();
      const duplicateName = existingModelFamilies.data.find(
        (family) => family.name?.toLowerCase().trim() === normalizedName && family.uuid !== uuid,
      );

      if (duplicateName) {
        hasDuplicateName = true;
        errors.push(t("masterdata:ModelFamilyNameAlreadyExists", { name: watchedName }));
      }
    }

    if (watchedDatId?.trim()) {
      const normalizedDatId = watchedDatId.toLowerCase().trim();
      const duplicateDatId = existingModelFamilies.data.find(
        (family) => getNormalizedModelFamilyDatId(family)?.toLowerCase() === normalizedDatId && family.uuid !== uuid,
      );

      if (duplicateDatId) {
        hasDuplicateDatId = true;
        errors.push(
          t("masterdata:ModelFamilyDatIdAlreadyExists", {
            datId: watchedDatId,
          }),
        );
      }
    }

    return {
      errors,
      hasErrors: errors.length > 0,
      hasDuplicateDatId,
      hasDuplicateName,
    };
  }, [existingModelFamilies?.data, t, uuid, watchedBrand, watchedDatId, watchedName]);

  const handleSubmitWithValidation = async (values: ModelFamilyFormSchemaType): Promise<void> => {
    if (duplicateValidation.hasErrors) {
      duplicateValidation.errors.forEach((error) => {
        toast.error(t("common:ValidationError"), {
          description: error,
          duration: VALIDATION_ERROR_DURATION,
        });
      });
      return;
    }

    const payload = {
      name: values.name.trim(),
      modelFamilyIdentifiers: values.datId?.trim()
        ? [{ identifier: values.datId.trim(), provider: ModelFamilyIdentifierProvider.Dat }]
        : [],
      brand: values.brand,
    };

    try {
      await toastMutationStatus({
        mutationFn: () =>
          isUpdateMode && uuid
            ? updateMutation.mutateAsync({
                uuid,
                ...payload,
              })
            : createMutation.mutateAsync(payload),
        successMessage: isUpdateMode
          ? t("masterdata:ModelFamilySuccessfullyUpdated")
          : t("masterdata:ModelFamilySuccessfullyCreated"),
        errorMessage: isUpdateMode
          ? t("masterdata:ErrorUpdatingModelFamily")
          : t("masterdata:ErrorCreatingModelFamily"),
      });

      navigate("~/masterdata/model-family");
    } catch (error) {
      console.error(`Error ${isUpdateMode ? "updating" : "creating"} model family:`, error);
    }
  };

  const isLoading = isBrandsLoading || (isUpdateMode && (isModelFamilyLoading || !modelFamily));
  const isPending = createMutation.isPending || updateMutation.isPending;
  const isCheckingDuplicates = isExistingModelFamiliesLoading && !!watchedBrand;
  const isSubmitDisabled =
    isPending || isCheckingDuplicates || duplicateValidation.hasErrors || !form.formState.isValid;

  if (isLoading) {
    return (
      <div className="space-y-6">
        {createPlaceholderKeys(3, "skeleton").map((key) => (
          <div key={key} className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        ))}

        <div className="flex justify-end space-x-4">
          <Skeleton className="h-10 w-24 rounded-md" />
          <Skeleton className="h-10 w-24 rounded-md" />
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmitWithValidation)}>
        <Card>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common:Name")} *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        placeholder={t("masterdata:ModelFamilyNameExample")}
                        error={duplicateValidation.hasDuplicateName}
                        disabled={isCheckingDuplicates}
                      />
                      {isCheckingDuplicates && (
                        <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                  {isCheckingDuplicates ? (
                    <p className="text-muted-foreground mt-1 text-sm">
                      {t("masterdata:CheckingDuplicateModelFamilyNames")}
                    </p>
                  ) : (
                    duplicateValidation.hasDuplicateName && (
                      <p className="text-error mt-1 text-sm">{t("masterdata:ModelFamilyNameDuplicateError")}</p>
                    )
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="brand"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>{t("common:Brand")} *</FormLabel>
                  <Combobox
                    options={brandOptions}
                    value={field.value}
                    onChange={field.onChange}
                    isLoading={isBrandsLoading}
                    placeholder={t("masterdata:SelectBrand")}
                    searchPlaceholder={t("common:SearchBrands")}
                    emptyText={t("common:NoBrandsFound")}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="datId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:ModelFamilyDatId")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        placeholder={t("masterdata:ModelFamilyDatIdPlaceholder")}
                        error={duplicateValidation.hasDuplicateDatId}
                        disabled={isCheckingDuplicates}
                      />
                      {isCheckingDuplicates && (
                        <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                  {isCheckingDuplicates ? (
                    <p className="text-muted-foreground mt-1 text-sm">{t("masterdata:CheckingDuplicateDatIds")}</p>
                  ) : (
                    duplicateValidation.hasDuplicateDatId && (
                      <p className="text-error mt-1 text-sm">{t("masterdata:ModelFamilyDatIdDuplicateError")}</p>
                    )
                  )}
                </FormItem>
              )}
            />

            {isUpdateMode && modelFamily && (
              <SystemInfoFields
                uuid={modelFamily.uuid}
                createdAt={modelFamily.createdAt}
                updatedAt={modelFamily.updatedAt}
              />
            )}
          </CardContent>
        </Card>

        <FormCardActions
          cancelLabel={t("common:Cancel")}
          onCancel={() => window.history.back()}
          isPending={isPending}
          submitDisabled={isSubmitDisabled}
          submitLabel={isUpdateMode ? t("common:SaveChanges") : t("common:Save")}
        />
      </form>
    </Form>
  );
}
